import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { DocumentUpload } from "@/components/DocumentUpload";
import { 
  ArrowRight, 
  ArrowLeft, 
  Upload, 
  MapPin, 
  DollarSign,
  Briefcase,
  Award,
  Target,
  Users,
  X
} from "lucide-react";
import { IntakeFormData } from "@/types/careerMatch";

interface CareerMatchIntakeProps {
  onComplete: (data: IntakeFormData) => void;
  onBack: () => void;
}

export function CareerMatchIntake({ onComplete, onBack }: CareerMatchIntakeProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  
  const [formData, setFormData] = useState<IntakeFormData>({
    resumeText: '',
    coverLetterText: '',
    portfolioLinks: [],
    notes: '',
    city: '',
    state: '',
    searchRadius: 25,
    workSetup: 'hybrid',
    salaryTarget: 80000,
    salaryMinimum: 60000,
    industriesToInclude: [],
    industriesToExclude: [],
    companySize: 'any',
    totalYearsExperience: 5,
    domainSpecificExperience: 3,
    managementExperience: false,
    teamSize: undefined,
    toolsAndPlatforms: [],
    certifications: [],
    educationLevel: "Bachelor's Degree",
    educationMajor: '',
    careerWin1: '',
    careerWin2: '',
    constraint1: '',
    constraint2: '',
    seedRole1: '',
    seedRole2: '',
    seedRole3: '',
    workType: 'permanent'
  });

  const [newPortfolioLink, setNewPortfolioLink] = useState('');
  const [newTool, setNewTool] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newIndustryInclude, setNewIndustryInclude] = useState('');
  const [newIndustryExclude, setNewIndustryExclude] = useState('');

  const updateFormData = (updates: Partial<IntakeFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const addToArray = (field: keyof IntakeFormData, value: string, setter: (val: string) => void) => {
    if (value.trim()) {
      const currentArray = formData[field] as string[];
      updateFormData({ [field]: [...currentArray, value.trim()] });
      setter('');
    }
  };

  const removeFromArray = (field: keyof IntakeFormData, index: number) => {
    const currentArray = formData[field] as string[];
    updateFormData({ [field]: currentArray.filter((_, i) => i !== index) });
  };

  const canProceedFromStep = (step: number): boolean => {
    switch (step) {
      case 1: return !!formData.resumeText;
      case 2: return !!formData.city && !!formData.state;
      case 3: return formData.salaryTarget > 0 && formData.salaryMinimum > 0;
      case 4: return formData.totalYearsExperience >= 0;
      case 5: return !!formData.educationLevel && !!formData.educationMajor;
      case 6: return !!formData.seedRole1;
      default: return true;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps && canProceedFromStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const handleComplete = () => {
    if (canProceedFromStep(currentStep)) {
      onComplete(formData);
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Upload className="w-12 h-12 text-primary mx-auto" />
              <h2 className="text-2xl font-bold">Upload Your Documents</h2>
              <p className="text-muted-foreground">Upload your resume and any additional documents</p>
            </div>
            
            <DocumentUpload
              onUploadComplete={(text, filename) => {
                updateFormData({ resumeText: text });
              }}
              title="Resume (Required)"
              description="Upload your current resume for analysis"
            />
            
            <DocumentUpload
              onUploadComplete={(text, filename) => {
                updateFormData({ coverLetterText: text });
              }}
              title="Cover Letter (Optional)"
              description="Upload a recent cover letter if available"
            />
            
            <div className="space-y-3">
              <Label>Portfolio Links</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://linkedin.com/in/yourname"
                  value={newPortfolioLink}
                  onChange={(e) => setNewPortfolioLink(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToArray('portfolioLinks', newPortfolioLink, setNewPortfolioLink)}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => addToArray('portfolioLinks', newPortfolioLink, setNewPortfolioLink)}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.portfolioLinks.map((link, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {link}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 w-4 h-4"
                      onClick={() => removeFromArray('portfolioLinks', index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any additional context about your career goals or background..."
                value={formData.notes}
                onChange={(e) => updateFormData({ notes: e.target.value })}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <MapPin className="w-12 h-12 text-primary mx-auto" />
              <h2 className="text-2xl font-bold">Location & Work Setup</h2>
              <p className="text-muted-foreground">Tell us about your location and work preferences</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="Houston"
                  value={formData.city}
                  onChange={(e) => updateFormData({ city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  placeholder="TX"
                  value={formData.state}
                  onChange={(e) => updateFormData({ state: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="searchRadius">Search Radius (miles)</Label>
              <Select value={formData.searchRadius.toString()} onValueChange={(value) => updateFormData({ searchRadius: parseInt(value) })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 miles</SelectItem>
                  <SelectItem value="25">25 miles</SelectItem>
                  <SelectItem value="50">50 miles</SelectItem>
                  <SelectItem value="100">100 miles</SelectItem>
                  <SelectItem value="0">Remote only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Work Setup Preference</Label>
              <Select value={formData.workSetup} onValueChange={(value: any) => updateFormData({ workSetup: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="remote">Remote</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="onsite">On-site</SelectItem>
                  <SelectItem value="flexible">Flexible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <DollarSign className="w-12 h-12 text-primary mx-auto" />
              <h2 className="text-2xl font-bold">Salary Expectations</h2>
              <p className="text-muted-foreground">Help us find roles that match your compensation needs</p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="salaryTarget">Target Salary</Label>
                <Input
                  id="salaryTarget"
                  type="number"
                  placeholder="80000"
                  value={formData.salaryTarget}
                  onChange={(e) => updateFormData({ salaryTarget: parseInt(e.target.value) || 0 })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="salaryMinimum">Minimum Acceptable Salary</Label>
                <Input
                  id="salaryMinimum"
                  type="number"
                  placeholder="60000"
                  value={formData.salaryMinimum}
                  onChange={(e) => updateFormData({ salaryMinimum: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-3">
                <Label>Industries to Include</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., Technology, Healthcare"
                    value={newIndustryInclude}
                    onChange={(e) => setNewIndustryInclude(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('industriesToInclude', newIndustryInclude, setNewIndustryInclude)}
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => addToArray('industriesToInclude', newIndustryInclude, setNewIndustryInclude)}
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.industriesToInclude.map((industry, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {industry}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 w-4 h-4"
                        onClick={() => removeFromArray('industriesToInclude', index)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3">
                <Label>Industries to Exclude</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., Retail, Food Service"
                    value={newIndustryExclude}
                    onChange={(e) => setNewIndustryExclude(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('industriesToExclude', newIndustryExclude, setNewIndustryExclude)}
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => addToArray('industriesToExclude', newIndustryExclude, setNewIndustryExclude)}
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.industriesToExclude.map((industry, index) => (
                    <Badge key={index} variant="destructive" className="flex items-center gap-1">
                      {industry}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 w-4 h-4"
                        onClick={() => removeFromArray('industriesToExclude', index)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Company Size Preference</Label>
                <Select value={formData.companySize} onValueChange={(value: any) => updateFormData({ companySize: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="startup">Startup (1-50 employees)</SelectItem>
                    <SelectItem value="small">Small (51-200 employees)</SelectItem>
                    <SelectItem value="medium">Medium (201-1000 employees)</SelectItem>
                    <SelectItem value="large">Large (1000-5000 employees)</SelectItem>
                    <SelectItem value="enterprise">Enterprise (5000+ employees)</SelectItem>
                    <SelectItem value="any">No preference</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Briefcase className="w-12 h-12 text-primary mx-auto" />
              <h2 className="text-2xl font-bold">Experience & Skills</h2>
              <p className="text-muted-foreground">Tell us about your professional background</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="totalExp">Total Years Experience</Label>
                <Input
                  id="totalExp"
                  type="number"
                  min="0"
                  value={formData.totalYearsExperience}
                  onChange={(e) => updateFormData({ totalYearsExperience: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="domainExp">Domain-Specific Experience</Label>
                <Input
                  id="domainExp"
                  type="number"
                  min="0"
                  value={formData.domainSpecificExperience}
                  onChange={(e) => updateFormData({ domainSpecificExperience: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="management"
                  checked={formData.managementExperience}
                  onCheckedChange={(checked) => updateFormData({ managementExperience: checked as boolean })}
                />
                <Label htmlFor="management">I have people management experience</Label>
              </div>
              
              {formData.managementExperience && (
                <div className="space-y-2 ml-6">
                  <Label htmlFor="teamSize">Team Size Managed</Label>
                  <Input
                    id="teamSize"
                    type="number"
                    min="1"
                    placeholder="5"
                    value={formData.teamSize || ''}
                    onChange={(e) => updateFormData({ teamSize: parseInt(e.target.value) || undefined })}
                  />
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <Label>Tools & Platforms Used Recently</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., Excel, Salesforce, Python"
                  value={newTool}
                  onChange={(e) => setNewTool(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToArray('toolsAndPlatforms', newTool, setNewTool)}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => addToArray('toolsAndPlatforms', newTool, setNewTool)}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.toolsAndPlatforms.map((tool, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {tool}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 w-4 h-4"
                      onClick={() => removeFromArray('toolsAndPlatforms', index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-3">
              <Label>Certifications</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., PMP, CPA, AWS Certified"
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToArray('certifications', newCertification, setNewCertification)}
                />
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => addToArray('certifications', newCertification, setNewCertification)}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.certifications.map((cert, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    {cert}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 w-4 h-4"
                      onClick={() => removeFromArray('certifications', index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Award className="w-12 h-12 text-primary mx-auto" />
              <h2 className="text-2xl font-bold">Education & Achievements</h2>
              <p className="text-muted-foreground">Share your educational background and career wins</p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Education Level</Label>
                <Select value={formData.educationLevel} onValueChange={(value) => updateFormData({ educationLevel: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High School">High School</SelectItem>
                    <SelectItem value="Associate's Degree">Associate's Degree</SelectItem>
                    <SelectItem value="Bachelor's Degree">Bachelor's Degree</SelectItem>
                    <SelectItem value="Master's Degree">Master's Degree</SelectItem>
                    <SelectItem value="Doctoral Degree">Doctoral Degree</SelectItem>
                    <SelectItem value="Professional Degree">Professional Degree</SelectItem>
                    <SelectItem value="Trade/Vocational">Trade/Vocational</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="educationMajor">Major/Field of Study</Label>
                <Input
                  id="educationMajor"
                  placeholder="e.g., Computer Science, Business Administration"
                  value={formData.educationMajor}
                  onChange={(e) => updateFormData({ educationMajor: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="careerWin1">Career Achievement #1 (with numbers)</Label>
                <Textarea
                  id="careerWin1"
                  placeholder="e.g., Increased sales by 25% ($2M) in first year by implementing new CRM system"
                  value={formData.careerWin1}
                  onChange={(e) => updateFormData({ careerWin1: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="careerWin2">Career Achievement #2 (with numbers)</Label>
                <Textarea
                  id="careerWin2"
                  placeholder="e.g., Led team of 8 to reduce processing time by 40%, saving company $500K annually"
                  value={formData.careerWin2}
                  onChange={(e) => updateFormData({ careerWin2: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="constraint1">Career Constraint #1</Label>
                <Input
                  id="constraint1"
                  placeholder="e.g., No travel more than 25%, Must be flexible schedule"
                  value={formData.constraint1}
                  onChange={(e) => updateFormData({ constraint1: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="constraint2">Career Constraint #2</Label>
                <Input
                  id="constraint2"
                  placeholder="e.g., No shift work, Must allow remote work"
                  value={formData.constraint2}
                  onChange={(e) => updateFormData({ constraint2: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Target className="w-12 h-12 text-primary mx-auto" />
              <h2 className="text-2xl font-bold">Role Preferences</h2>
              <p className="text-muted-foreground">Tell us about your ideal roles and work type</p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="seedRole1">Role That Interests You #1 (Required)</Label>
                <Input
                  id="seedRole1"
                  placeholder="e.g., Data Analyst"
                  value={formData.seedRole1}
                  onChange={(e) => updateFormData({ seedRole1: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="seedRole2">Role That Interests You #2</Label>
                <Input
                  id="seedRole2"
                  placeholder="e.g., Business Intelligence Specialist"
                  value={formData.seedRole2}
                  onChange={(e) => updateFormData({ seedRole2: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="seedRole3">Role That Interests You #3</Label>
                <Input
                  id="seedRole3"
                  placeholder="e.g., Marketing Analyst"
                  value={formData.seedRole3}
                  onChange={(e) => updateFormData({ seedRole3: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Work Type Preference</Label>
              <Select value={formData.workType} onValueChange={(value: any) => updateFormData({ workType: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="permanent">Permanent/Full-time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="contract-to-hire">Contract-to-hire</SelectItem>
                  <SelectItem value="flexible">Flexible/Open to both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="text-center">
            <div className="text-sm text-muted-foreground">Step {currentStep} of {totalSteps}</div>
            <div className="text-lg font-semibold">Career Match Assessment</div>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Progress */}
      <div className="border-b border-border py-4">
        <div className="container mx-auto px-4">
          <Progress value={progressPercentage} className="w-full" />
        </div>
      </div>

      {/* Content */}
      <main className="py-8 px-4">
        <div className="container mx-auto max-w-2xl">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground mb-2">
                <Users className="w-4 h-4" />
                <span>Step {currentStep} of {totalSteps}</span>
              </div>
            </CardHeader>
            <CardContent>
              {renderStepContent()}
              
              <div className="flex justify-between mt-8">
                <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                
                {currentStep < totalSteps ? (
                  <Button 
                    onClick={handleNext}
                    disabled={!canProceedFromStep(currentStep)}
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleComplete}
                    disabled={!canProceedFromStep(currentStep)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    Generate Report
                    <Target className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}